import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

// Error message display duration
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an individual product in the Purchase in Bulk block.
 * Handles add to cart functionality for bulk bundle products.
 *
 * @typedef {Object} PurchaseInBulkProductRefs
 * @property {HTMLFormElement} [form] - The product form
 * @property {HTMLInputElement} [variantIdInput] - The variant ID input
 * @property {HTMLButtonElement} [addToCartButton] - The add to cart button
 * @property {HTMLElement} [addToCartText] - The add to cart text
 * @property {HTMLElement} [addToCartTextError] - The add to cart error element
 * @property {HTMLImageElement | SVGElement} [productImage] - The product image
 *
 * @extends {Component<PurchaseInBulkProductRefs>}
 */
export class PurchaseInBulkProductComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {HTMLFormElement | null} */
  #currentForm = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Initialize form submission handler
    this.#initializeFormSubmission(signal);

    // Watch for form changes (in case form is morphed without triggering updatedCallback)
    this.#watchForFormChanges(signal);

    // Preload image on hover
    this.addEventListener('pointerenter', this.#preloadImage, { signal });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API or after morphing.
   * Reinitializes form submission if the form ref has changed.
   */
  updatedCallback() {
    super.updatedCallback();

    // Use queueMicrotask to ensure refs are updated by MutationObserver first
    queueMicrotask(() => {
      // Check if form ref has changed and reinitialize if needed
      const { form } = this.refs;
      if (form && form !== this.#currentForm) {
        // Form has changed, reinitialize submission handler
        if (this.#abortController) {
          this.#abortController.abort();
        }
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;
        this.#initializeFormSubmission(signal);
        this.#currentForm = form;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    // Clear timeouts
    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this.#resetTimeouts = [];
    }

    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
    }
  }

  /**
   * Watch for form element changes and reinitialize submission handler if needed
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #watchForFormChanges(signal) {
    // Use MutationObserver to watch for form element changes
    const observer = new MutationObserver((mutations) => {
      // Only check if forms were added or removed
      const hasFormChanges = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some(
          (node) => node instanceof HTMLFormElement || node.querySelector?.('form')
        ) || Array.from(mutation.removedNodes).some(
          (node) => node instanceof HTMLFormElement || node.querySelector?.('form')
        );
      });

      if (hasFormChanges) {
        // Use queueMicrotask to ensure refs are updated first
        queueMicrotask(() => {
          const { form } = this.refs;
          if (form && form !== this.#currentForm) {
            // Form has changed, reinitialize submission handler
            if (this.#abortController) {
              this.#abortController.abort();
            }
            this.#abortController = new AbortController();
            const newSignal = this.#abortController.signal;
            this.#initializeFormSubmission(newSignal);
          }
        });
      }
    });

    // Watch for changes in the component's subtree
    observer.observe(this, {
      childList: true,
      subtree: true,
    });

    // Clean up observer when signal is aborted
    signal.addEventListener('abort', () => {
      observer.disconnect();
    });
  }

  /**
   * Public method to reinitialize form submission handler
   * Called after morphing to ensure form handlers are reattached
   */
  reinitializeFormSubmission() {
    const { form } = this.refs;
    if (!form) return;

    // If form hasn't changed, no need to reinitialize
    if (form === this.#currentForm) return;

    // Abort old controller and create new one
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#initializeFormSubmission(signal);
  }

  /**
   * Initialize form submission handler
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #initializeFormSubmission(signal) {
    const { form } = this.refs;
    if (!form) return;

    // Store reference to current form
    this.#currentForm = form;

    form.addEventListener('submit', (event) => {
      this.#handleFormSubmit(event);
    }, { signal });
  }

  /**
   * Preload product image on hover
   */
  #preloadImage = () => {
    const { productImage } = this.refs;
    if (!productImage || !(productImage instanceof HTMLImageElement)) return;

    const imageUrl = productImage.src;
    if (imageUrl) {
      preloadImage(imageUrl);
    }
  };

  /**
   * Handle form submission with AJAX
   * @param {Event} event - The submit event
   */
  #handleFormSubmit(event) {
    event.preventDefault();
    const { form, addToCartButton, variantIdInput } = this.refs;

    if (!form || !addToCartButton || !variantIdInput) return;

    // Check form validity
    if (!form.checkValidity()) return;

    // Check if button is disabled
    if (addToCartButton.disabled) return;

    // Disable button during submission
    addToCartButton.disabled = true;

    // Trigger animations before AJAX call
    this.#animateAddToCart();

    // Prepare form data
    const formData = new FormData(form);
    const plainObject = Object.fromEntries(formData.entries());
    /** @type {{ items: Array<{ id: number; quantity: number }>; sections?: string }} */
    const items = {
      items: [
        {
          id: parseInt(String(plainObject.id || '0')),
          quantity: parseInt(String(plainObject.quantity || '1'))
        }
      ]
    };

    // Get cart sections to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }
    if (cartItemComponentsSectionIds.length > 0) {
      items.sections = cartItemComponentsSectionIds.join(',');
    }

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(items) });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          // Handle error response from cart API (e.g. inventory limits, validation errors)
          this.#handleAddToCartError(response.message || response.description);
          this.dispatchEvent(
            new CartErrorEvent(form.id || '', response.message, response.description, response.errors)
          );
        } else {
          // Handle success
          await this.#handleAddToCartSuccess(response, formData, items);
        }
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
        this.#handleAddToCartError('An error occurred. Please try again.');
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Handle successful add to cart
   * @param {{ sections?: string }} response - The cart response
   * @param {FormData} formData - The form data
   * @param {Object} items - The items object
   */
  async #handleAddToCartSuccess(response, formData, items) {
    const { addToCartButton } = this.refs;
    const variantId = formData.get('id');

    if (!variantId) return;

    // Success - button animation is handled by #animateAddToCart via data-added attribute

    // Dispatch cart add event
    this.dispatchEvent(
      new CartAddEvent({}, variantId.toString(), {
        source: 'purchase-in-bulk-product',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: this.dataset.productId,
        sections: response.sections ? /** @type {Record<string, string>} */ (/** @type {unknown} */ (response.sections)) : undefined,
      })
    );

    // Re-enable button after a short delay
    setTimeout(() => {
      if (addToCartButton) {
        addToCartButton.disabled = false;
      }
    }, 500);
  }

  /**
   * Handle add to cart error - displays the error message inline next to the button
   * and re-enables the add to cart button after a short delay.
   * @param {string} errorMessage - The error message to show to the customer
   */
  #handleAddToCartError(errorMessage) {
    const { addToCartButton, addToCartTextError } = this.refs;

    if (addToCartTextError) {
      // Reset any leftover added-to-cart animation state so the error is visible
      addToCartButton?.removeAttribute('data-added');
      addToCartTextError.classList.remove('hidden');

      // Reuse a stable text node for the message so rapid retries update in place
      // instead of appending duplicate spans.
      let errorText = addToCartTextError.querySelector('.product-form-error-text');
      if (!errorText) {
        errorText = document.createElement('span');
        errorText.classList.add('product-form-error-text');
        addToCartTextError.appendChild(errorText);
      }
      this.classList.add('purchase-in-bulk__product--error');
      errorText.textContent = errorMessage || '';

      if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
      this.#errorTimeout = window.setTimeout(() => {
        addToCartTextError.classList.add('hidden');
        const errorTextEl = addToCartTextError.querySelector('.product-form-error-text');
        if (errorTextEl) {
          errorTextEl.textContent = '';
          this.classList.remove('purchase-in-bulk__product--error');
        }
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    // Always re-enable the button after the delay, even if the error markup is missing,
    // so the user can retry.
    setTimeout(() => {
      if (addToCartButton) addToCartButton.disabled = false;
    }, ERROR_BUTTON_REENABLE_DELAY);
  }


  /**
   * Animate the add to cart button
   */
  #animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach((/** @type {number} */ timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('purchase-in-bulk-product')) {
  customElements.define('purchase-in-bulk-product', PurchaseInBulkProductComponent);
}

/**
 * A custom element that manages the Purchase in Bulk block container.
 * Acts as a parent component that contains multiple product components.
 *
 * @extends {Component}
 */
export class PurchaseInBulkComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Parent component doesn't need specific initialization
    // Child product components handle their own functionality
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
}

if (!customElements.get('purchase-in-bulk-component')) {
  customElements.define('purchase-in-bulk-component', PurchaseInBulkComponent);
}
